'use client'

import { useEffect, useState } from 'react'

import { isChunkLoadError, recoverFromChunkError } from '@/common/helpers/chunkErrorRecovery'
import { getAppInsights } from '@/core/providers/ConfigAppInsightsContainer/AppInsights'

// Fallback cuando el error ocurre en el propio root layout (providers, etc.):
// reemplaza al layout completo, por eso declara <html>/<body> y no usa estilos globales
interface IGlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

const estiloBoton = (principal: boolean): React.CSSProperties => ({
  padding: '0.5rem 1.25rem',
  borderRadius: '0.375rem',
  border: principal ? 'none' : '1px solid #ced4da',
  backgroundColor: principal ? '#556ee6' : '#ffffff',
  color: principal ? '#ffffff' : '#212529',
  fontSize: '1rem',
  cursor: 'pointer',
})

const GlobalError: React.FC<IGlobalErrorProps> = ({ error }) => {
  const [recargando, setRecargando] = useState(false)

  useEffect(() => {
    // Igual que el boundary de segmento: un ChunkLoadError tras un deploy se resuelve solo con un
    // reload, porque el navegador conserva el manifest del build anterior. Acá faltaba, y era el
    // caso peor: si el chunk que falla es del root layout, cae en esta pantalla y el boton de
    // reintentar no podia servir de nada.
    if (isChunkLoadError(error) && recoverFromChunkError()) {
      setRecargando(true)

      return
    }

    // El error se ignoraba por completo: no se registraba ni se mostraba, asi que ni el usuario ni
    // la mesa de ayuda tenian con que empezar. App Insights puede no estar inicializado —si fallo
    // el root layout, su provider quizas nunca se monto—, por eso ademas siempre va a consola.
    getAppInsights()?.trackException({ exception: error })
    console.error('Error capturado por el boundary global:', error)
  }, [error])

  const contenido = recargando ? (
    <p style={{ margin: 0, color: '#6c757d' }}>Hay una nueva versión disponible, actualizando…</p>
  ) : (
    <>
      <h2 style={{ margin: 0 }}>Ocurrió un error inesperado</h2>
      <p style={{ margin: 0, color: '#6c757d', maxWidth: '30rem', textAlign: 'center' }}>
        Recargue la página. Si el problema persiste contacte a TI a través de la mesa de ayuda.
      </p>
      {error.digest !== undefined && (
        // El digest es la unica forma de encontrar el detalle del lado del servidor: sin el, el
        // reporte a la mesa de ayuda es "salio un error" y no hay por donde agarrarlo.
        <p style={{ margin: 0, color: '#6c757d', fontSize: '0.875rem' }}>
          {`Código del error: ${error.digest}`}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* Recargar y no `reset()`: acá el que falló es el layout raíz, así que volver a renderizarlo
            reconstruye el mismo árbol que se acaba de romper y la pantalla no cambia. Eso es lo que
            hacía que el botón pareciera muerto. Un reload sí trae HTML y chunks nuevos. */}
        <button onClick={() => window.location.reload()} style={estiloBoton(true)}>
          Recargar la página
        </button>
        {/* Sin `next/navigation`: el router vive en el layout que fallo. */}
        <button onClick={() => window.location.assign('/')} style={estiloBoton(false)}>
          Ir al inicio
        </button>
      </div>
    </>
  )

  return (
    <html lang="es">
      <body
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '1rem',
          fontFamily: 'system-ui, sans-serif',
          backgroundColor: '#f1f2f2',
          color: '#212529',
          margin: 0,
        }}
      >
        {contenido}
      </body>
    </html>
  )
}

export default GlobalError
